import * as React from 'react';
import { cn } from '@/lib/utils';

interface LinearProgressBarProps {
  /** If undefined, renders an indeterminate pulse (the existing default). */
  value?: number; // 0-100
  /** Bar height + width overrides. Defaults to 'h-1 w-full'. */
  className?: string;
  /** Tone of the filled portion. Defaults to bg-primary. */
  barClass?: string;
}

/**
 * Inline progress bar used to indicate "something is loading at the top of
 * this page" without claiming space. Promoted from a 33-copy inline pattern
 * — see AdminPage's local copy, TenantBillingPage's loading bar, etc.
 *
 * When `value` is undefined we keep the historical indeterminate behaviour
 * (`w-1/3 animate-pulse`) so we can swap consumers in without visual churn.
 * For determinate progress with a transition, use the existing `<Progress />`
 * primitive — this one is intentionally simpler.
 */
export function LinearProgressBar({ value, className, barClass }: LinearProgressBarProps) {
  const isIndeterminate = value === undefined;
  const clamped = isIndeterminate ? 0 : Math.min(100, Math.max(0, value));

  return (
    <div
      className={cn('h-1 w-full overflow-hidden rounded-full bg-muted', className)}
      role={isIndeterminate ? undefined : 'progressbar'}
      aria-valuenow={isIndeterminate ? undefined : clamped}
      aria-valuemin={isIndeterminate ? undefined : 0}
      aria-valuemax={isIndeterminate ? undefined : 100}
    >
      <div
        className={cn(
          'h-full bg-primary',
          isIndeterminate ? 'w-1/3 animate-pulse' : null,
          barClass,
        )}
        style={isIndeterminate ? undefined : { width: `${clamped}%` }}
      />
    </div>
  );
}
